import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@timeedit/ui-components';
import type { ExpandableConfig } from '@timeedit/ui-components/antd/table/interface';

type UseExpandableOptions<T extends string | number = string> = {
  /**
   * Keys to expand on initial load. Once set, the hook fully controls expansion state.
   * When provided, `defaultExpandAllRows` is not used.
   */
  initialKeys?: T[];
};

export const useExpandable = <T extends string | number = string>(
  options?: UseExpandableOptions<T>
): {
  expandable: ExpandableConfig<object>;
  expandedRowKeys: T[];
  setExpandedRowKeys: (keys: T[]) => void;
  expandAll: (nodeId: T, childIds: T[]) => void;
  collapseAll: (nodeId: T, childIds: T[]) => void;
} => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<T[]>([]);
  // Track whether the user has interacted with expansion state
  const userHasInteracted = useRef(false);

  // Keep syncing initialKeys until the user interacts
  useEffect(() => {
    if (userHasInteracted.current) return;
    const keys = options?.initialKeys;
    if (!keys || keys.length === 0) return;
    setExpandedRowKeys(keys);
  }, [options?.initialKeys]);

  const handleUserExpand = useCallback((keys: T[]) => {
    userHasInteracted.current = true;
    setExpandedRowKeys(keys);
  }, []);

  const expandAll = useCallback(
    (nodeId: T, childIds: T[]) => {
      setExpandedRowKeys((prev) => {
        const currentKeys =
          !userHasInteracted.current && prev.length === 0
            ? (options?.initialKeys ?? [])
            : prev;
        userHasInteracted.current = true;
        return Array.from(new Set([nodeId, ...childIds, ...currentKeys]));
      });
    },
    [options?.initialKeys]
  );

  const collapseAll = useCallback(
    (nodeId: T, childIds: T[]) => {
      setExpandedRowKeys((prev) => {
        const currentKeys =
          !userHasInteracted.current && prev.length === 0
            ? (options?.initialKeys ?? [])
            : prev;
        userHasInteracted.current = true;
        const toRemove = new Set<T>([nodeId, ...childIds]);
        return currentKeys.filter((key) => !toRemove.has(key));
      });
    },
    [options?.initialKeys]
  );

  const expandable = useMemo(() => {
    // Only use defaultExpandAllRows when no initialKeys are provided (backward compat)
    const useDefault = !options?.initialKeys;
    return {
      ...(useDefault ? { defaultExpandAllRows: true } : {}),
      expandIcon: ({
        expanded,
        onExpand,
        record,
      }: {
        expanded: boolean;
        onExpand: (record: any, e: React.MouseEvent) => void;
        record: any;
      }) => {
        if (record.children && record.children.length > 0) {
          return (
            <button
              type="button"
              className={'te-expand-icon'}
              aria-label={expanded ? 'Collapse row' : 'Expand row'}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              style={{
                marginLeft: record.width,
                marginRight: 8,
              }}
            >
              <Icon
                id="CaretDownOutlined"
                alt="Caret Down"
                style={{
                  transform: `rotate(${expanded ? '0deg' : '-90deg'})`,
                  transitionDuration: '0.4s',
                }}
              />
            </button>
          );
        }
        // Invisible button for rows without children for alignment purposes
        return (
          <button
            style={{
              marginLeft: record.width,
              visibility: 'hidden',
              marginRight: 8,
            }}
            className={'te-expand-icon'}
            type="button"
          />
        );
      },
      expandedRowKeys,
      onExpandedRowsChange: (listKey: T[]) => handleUserExpand(listKey),
    };
  }, [expandedRowKeys, options?.initialKeys, handleUserExpand]);

  return {
    expandable,
    expandedRowKeys,
    setExpandedRowKeys: handleUserExpand,
    expandAll,
    collapseAll,
  };
};
